import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';

// The three visit transitions that take an optional bit of text: reject
// (a reason) and check-in / check-out (a gate name). One dialog covers all
// three because the shape is identical — a short optional field and a confirm.
//
// The field is never required: the API accepts an empty body for all three, and
// check-in falls back to the gate already recorded on the visit.
const GateActionDialog = ({
    open,
    title,
    subtitle,
    fieldLabel,
    fieldHint,
    placeholder,
    maxLength = 255,
    multiline = false,
    confirmLabel,
    busyLabel,
    icon,
    tone = 'tw-bg-sky-600 hover:tw-bg-sky-700',
    busy = false,
    initialValue = '',
    onCancel,
    onConfirm,
}) => {
    const [value, setValue] = useState(initialValue);

    // A fresh value each time the dialog opens — carrying the last reason over
    // would quietly attach it to the wrong visit.
    useEffect(() => { if (open) setValue(initialValue); }, [open, initialValue]);

    if (!open) return null;

    const Field = multiline ? 'textarea' : 'input';

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-overflow-hidden">
                <div className="tw-p-4 tw-border-b tw-border-gray-100">
                    <h3 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-gray-800">{title}</h3>
                    {subtitle && <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{subtitle}</p>}
                </div>

                <div className="tw-p-5 tw-space-y-4">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">
                            {fieldLabel}
                            {fieldHint && <span className="tw-font-normal tw-text-gray-400"> — {fieldHint}</span>}
                        </label>
                        <Field
                            type={multiline ? undefined : 'text'}
                            rows={multiline ? 3 : undefined}
                            value={value}
                            onChange={e => setValue(e.target.value)}
                            maxLength={maxLength}
                            placeholder={placeholder}
                            className={`tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500 ${multiline ? 'tw-resize-none' : ''}`}
                        />
                        <p className="tw-mb-0 tw-text-[10px] tw-text-gray-400 tw-mt-1">{value.length}/{maxLength}</p>
                    </div>

                    <div className="tw-flex tw-justify-end tw-gap-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => onConfirm(value.trim())}
                            disabled={busy}
                            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-rounded-md disabled:tw-opacity-50 ${tone}`}
                        >
                            {icon && <Icon icon={icon} className="tw-text-sm" />}
                            {busy ? (busyLabel || 'Working...') : confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GateActionDialog;
